import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import gql from 'graphql-tag'
import dynamic from 'next/dynamic'
import { OpenGraphImage } from 'open-graph-scraper'
import styled from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
import { useNewsEditorLazyQuery } from 'generated/graphql'
import { parseDomainFromUrl } from '@app.modules/util'
import FileUploadDialog, { FileInfo } from 'components/FileUploadDialog'
import { DOCUMENT_TO_MIME_TYPES, DOCUMENT_TYPE } from 'constants/mimeTypes'
import { GetOpenGraphFromUrlReturns } from 'pages/api/ogs'
import { EditorProps, EditorRef } from './Editor'
import ImageContentViewer from './components/ImageContentViewer'
import NewsEditorActions from './components/NewsEditorActions'
import { OpenGraphPreview } from './components/OpenGraphPreview'
import OpenGraphPreviewError from './components/OpenGraphPreview/OpenGraphPreviewError'
import OpenGraphPreviewLoading from './components/OpenGraphPreview/OpenGraphPreviewLoading'
import WriterInfo from './components/WriterInfo'
import hashtag from './modules/constants/hashtag'
import { getEditorLength } from './modules/editor'

const Editor = dynamic(() => import('./Editor'), { ssr: false })

const EditorWithForwardedRef = forwardRef<EditorRef, EditorProps>(
  (props, ref) => <Editor {...props} forwardedRef={ref} />,
)

export interface IFEmbedContent {
  domain: string
  url: string
  description: string
  imageUrl?: string
}
interface NewsEditorProps {
  setIsCanSubmit: Dispatch<SetStateAction<boolean>>
  setIsDirty?: Dispatch<SetStateAction<boolean>>
  pictureMaxSizeMb?: number
  initialContent?: string
  initialImages?: FileInfo[]
  initialEmbed?: IFEmbedContent
  isModify?: boolean
  className?: string
}

export interface NewsEditorRef extends EditorRef {
  uploadedImageAttachmentIds: string[]
  embedContent?: IFEmbedContent | null
}

const FILE_UPLOAD_ID = 'news-picture-dialog'

gql`
  fragment NewsEditor_attachment on Attachment {
    id
    url
  }
  fragment NewsEditor_organization on Organization {
    id
    logoImage {
      ...NewsEditor_attachment
    }
  }
  fragment NewsEditor_channel on Channel {
    id
    name
    owner {
      ...NewsEditor_organization
    }
  }
  fragment NewsEditor_user on User {
    id
    channel {
      ...NewsEditor_channel
    }
  }

  query NewsEditor {
    currentUser {
      ...NewsEditor_user
    }
  }
`

const NewsEditor = forwardRef<NewsEditorRef, NewsEditorProps>(
  (
    {
      setIsCanSubmit,
      setIsDirty,
      pictureMaxSizeMb = 10,
      initialContent,
      initialEmbed,
      initialImages = [],
      isModify = false,
      className,
    },
    ref,
  ) => {
    const editorRef = useRef<EditorRef>(null)
    const ogPreview = useRef<GetOpenGraphFromUrlReturns | false>(false)
    const isCanSetOgPreview = useRef<boolean>(!isModify)
    const forBlockDuplicateUrlAdded = useRef<boolean>(false)

    const [loading, setLoading] = useState<boolean | null>(false)
    const [uploadedPictures, setUploadedPictures] =
      useState<FileInfo[]>(initialImages)
    const [overflowLength, setOverflowLength] = useState<number>(0)

    const [
      newsEditorQuery,
      { data: newsEditorData, loading: newsEditorLoading },
    ] = useNewsEditorLazyQuery()
    const TOTAL_FILESIZE_LIMIT = pictureMaxSizeMb * 1000 * 1000

    const handleResetOgPreview = () => {
      ogPreview.current = false
      setLoading(null)
    }

    const handleSetOgPreview = (ogResult: GetOpenGraphFromUrlReturns) => {
      if (
        isCanSetOgPreview.current &&
        (!ogPreview.current || ogPreview.current?.error)
      ) {
        ogPreview.current = ogResult
      }
    }

    const [debounceFetchOgData] = useDebouncedCallback(
      async (url: string, cb: (result: GetOpenGraphFromUrlReturns) => void) => {
        const data = await fetch(`/api/ogs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ url }),
        })
        const result = await data.json()
        cb(result)
      },
      300,
    )

    const handleUrlAdded = (url: string) => {
      if (
        isCanSetOgPreview.current &&
        (!ogPreview.current || ogPreview.current?.error) &&
        !forBlockDuplicateUrlAdded.current
      ) {
        setLoading(true)
        setTimeout(() => {
          setIsCanSubmit(false)
        }, 10) // 에디터 onChange에서 호출한 이후에 호출되도록 하기 위함

        forBlockDuplicateUrlAdded.current = true
        debounceFetchOgData(url, (result) => {
          handleSetOgPreview(result)
          setLoading(false)
          setIsCanSubmit(true)
          forBlockDuplicateUrlAdded.current = false
        })
      }
    }

    const handleFileInfo = (files: FileInfo[]) => {
      setUploadedPictures(files)
      isCanSetOgPreview.current = false
    }

    const handleFilesDelete = () => {
      setUploadedPictures([])
      isCanSetOgPreview.current = true
    }

    const handleApplyTags = (tags: string[]) => {
      const lastIndexOfEditor = editorRef.current?.quill?.getLength() || 1
      tags.map((tag) => {
        return editorRef.current?.quill?.insertEmbed(
          lastIndexOfEditor,
          hashtag.BLOT_NAME,
          tag,
          'api',
        )
      })
    }

    const parseOgPreviewData = (
      ogPreview: GetOpenGraphFromUrlReturns['result'],
    ): IFEmbedContent => {
      let ogImage: OpenGraphImage | undefined
      if (Array.isArray(ogPreview?.ogImage)) {
        ogImage = ogPreview?.ogImage?.[0]
      } else {
        ogImage = ogPreview?.ogImage
      }
      return {
        imageUrl: ogImage?.url,
        domain: parseDomainFromUrl(
          ogPreview?.ogUrl || ogPreview?.requestUrl || '',
        ),
        url: ogPreview?.ogUrl || ogPreview?.requestUrl || '',
        description: ogPreview?.ogDescription || ogPreview?.ogTitle || '',
      }
    }

    const handleGetValue = () => {
      return editorRef.current?.getValue() || ''
    }

    useImperativeHandle(ref, () => {
      return {
        quill: editorRef.current?.quill,
        getValue: handleGetValue,
        uploadedImageAttachmentIds: uploadedPictures.map(
          (picture) => picture.attachmentID,
        ),
        embedContent:
          ogPreview.current === false || ogPreview.current.error
            ? null
            : parseOgPreviewData(ogPreview.current.result),
      }
    })

    useEffect(() => {
      // TODO 더 깔끔하게 ref에 접근할 수 있는 방법으로 개선해보기
      setTimeout(() => {
        editorRef.current?.quill?.on('text-change', (delta, oldDelta) => {
          const textLength = getEditorLength(delta, oldDelta)

          if (textLength > 3000) {
            setOverflowLength(textLength)
          } else {
            setOverflowLength(0)
          }

          setIsCanSubmit(!loading && textLength >= 1 && textLength < 3001)
          setIsDirty && setIsDirty(true)
        })
      }, 500)
    }, [
      editorRef.current?.quill,
      setIsCanSubmit,
      setIsDirty,
      setOverflowLength,
      loading,
    ])

    useEffect(() => {
      newsEditorQuery()
    }, [newsEditorQuery])

    return (
      <StyledWrapper className={className}>
        <div className="scroll-wrapper">
          <WriterInfo
            logoImageUrl={
              newsEditorData?.currentUser?.channel?.owner?.logoImage?.url
            }
            name={newsEditorData?.currentUser?.channel?.name}
            isLoading={newsEditorLoading || !newsEditorData?.currentUser}
            className="writer-info"
          />
          <EditorWithForwardedRef
            ref={editorRef}
            onUrlAdded={handleUrlAdded}
            placeholder={`사진, 텍스트, 태그 등을 이용해 새로운 소식을 알려주세요.
팝업 하단의 ‘추천태그’를 선택하거나 샵(#)을 붙여 텍스트 입력 시
이 태그에 관심있는 이용자에게 더 효율적으로 소식을 노출할 수 있습니다.
(1자 이상 등록)
          `}
            initialValue={initialContent}
          />
          {uploadedPictures.length === 0 && (
            <div className="og-preview-wrapper">
              {loading && <OpenGraphPreviewLoading />}
              {ogPreview.current !== false &&
                !loading &&
                (ogPreview.current.error ? (
                  <OpenGraphPreviewError />
                ) : (
                  <OpenGraphPreview
                    imageSrc={
                      parseOgPreviewData(ogPreview.current.result).imageUrl
                    }
                    domain={parseOgPreviewData(ogPreview.current.result).domain}
                    url={parseOgPreviewData(ogPreview.current.result).url}
                    description={
                      parseOgPreviewData(ogPreview.current.result).description
                    }
                    onClose={handleResetOgPreview}
                  />
                ))}
              {initialEmbed && (
                <OpenGraphPreview
                  imageSrc={initialEmbed.imageUrl}
                  domain={initialEmbed.domain}
                  url={initialEmbed.url}
                  description={initialEmbed.description}
                />
              )}
            </div>
          )}
          {uploadedPictures.length !== 0 && (
            <ImageContentViewer
              imageSrcList={uploadedPictures.map((picture) => picture.url)}
              {...(!initialImages.length && {
                onDelete: handleFilesDelete,
              })}
            />
          )}
        </div>
        {overflowLength > 0 && (
          <div className="overflow-helper-text">
            최대 3000자까지 입력할 수 있습니다. (현재 {overflowLength}자)
          </div>
        )}
        <NewsEditorActions
          fileUploadDialogId={FILE_UPLOAD_ID}
          pictureButtonDisabled={
            isModify ||
            uploadedPictures.length !== 0 ||
            !(!ogPreview.current || ogPreview.current?.error) ||
            !!loading
          }
          onApplyTags={handleApplyTags}
          className="actions"
        />
        <FileUploadDialog
          id={FILE_UPLOAD_ID}
          totalFileSizeLimit={TOTAL_FILESIZE_LIMIT}
          path="/channel/news"
          getFileInfo={handleFileInfo}
          allowedFileTypes={DOCUMENT_TO_MIME_TYPES[DOCUMENT_TYPE.IMAGE]}
          maxNumberOfFiles={10}
        />
      </StyledWrapper>
    )
  },
)

export default NewsEditor

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  .scroll-wrapper {
    max-height: 434px;
    overflow-y: scroll;
    padding-right: 40px;
    @media screen and (max-height: 750px) {
      max-height: 400px;
    }
    @media screen and (max-height: 700px) {
      max-height: 350px;
    }
    @media screen and (max-height: 650px) {
      max-height: 300px;
    }
    @media screen and (max-height: 600px) {
      max-height: 250px;
    }
  }

  .writer-info {
    margin-bottom: 12px;
  }

  .og-preview-wrapper {
    margin-top: 24px;
    margin-bottom: 8px;
  }

  .actions {
    width: 100%;
    padding-top: 8px;
    background-color: white;
  }

  .overflow-helper-text {
    color: #ef2929;
    font-size: 14px;
    padding-top: 8px;
  }
`
